import type Lenis from 'lenis'
import { EASE, gsap } from '@/lib/gsap'
import { clamp } from '@/lib/math'

/** Attribute marking an element the nav focused after scrolling to it (styled in Nav.module.css). */
export const NAV_FOCUS_ATTR = 'data-nav-focus'

const easeInOut = gsap.parseEase(EASE.inOut)

/** Options for {@link scrollToSection}. */
export interface ScrollToSectionOptions {
  /** Jump instead of gliding (reduced motion, or when a menu covers the page). */
  immediate: boolean
  /** Called once the scroll has landed. */
  onComplete?: () => void
}

function focusSection(target: HTMLElement): void {
  // Move the keyboard to the section too, without a ring around the whole block.
  if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) {
    target.setAttribute('tabindex', '-1')
  }
  target.setAttribute(NAV_FOCUS_ATTR, '')
  target.focus({ preventScroll: true })
}

/**
 * Glides to an in-page anchor with Lenis (duration grows gently with distance), then moves
 * focus to the target. Returns `false` when the target doesn't exist.
 */
export function scrollToSection(
  lenis: Lenis | undefined,
  href: string,
  { immediate, onComplete }: ScrollToSectionOptions,
): boolean {
  const id = href.startsWith('#') ? href.slice(1) : href
  const target = id ? document.getElementById(id) : null
  if (!target) return false

  const done = () => {
    focusSection(target)
    onComplete?.()
  }

  if (!lenis) {
    target.scrollIntoView({ behavior: immediate ? 'auto' : 'smooth', block: 'start' })
    done()
    return true
  }

  // One layout read on click (never in a scroll handler).
  const distance = Math.abs(target.getBoundingClientRect().top)
  lenis.scrollTo(target, {
    immediate,
    force: true,
    duration: clamp(1 + distance / 4200, 1.1, 2.4),
    easing: easeInOut,
    onComplete: done,
  })
  return true
}
